import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Monitor, MapPin, Wifi, WifiOff, Search, Video } from 'lucide-react';
import { toast } from 'sonner';
import { API, useAuth, canEdit } from '../App';

const KioskList = () => {
  const [kiosks, setKiosks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [newKiosk, setNewKiosk] = useState({ name: '', location: '' });
  const [creating, setCreating] = useState(false);
  
  const { token, user } = useAuth();
  const editable = canEdit(user, 'booths');
  const navigate = useNavigate();

  const fetchKiosks = async () => {
    try {
      const response = await axios.get(`${API}/kiosks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKiosks(response.data);
    } catch (err) {
      console.error('Failed to fetch kiosks:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchKiosks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API}/kiosks`, newKiosk, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowCreateModal(false);
      setNewKiosk({ name: '', location: '' });
      fetchKiosks();
      toast.success('Booth created');
    } catch (err) {
      console.error('Failed to create kiosk:', err);
      toast.error(err.response?.data?.detail || 'Failed to create booth');
    }
    setCreating(false);
  };

  const filteredKiosks = kiosks.filter(k => 
    k.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    k.location.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="kiosk-list-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl text-charcoal-900">Booths</h1>
          <p className="text-charcoal-800/60 mt-1 text-sm sm:text-base">{kiosks.length} photobooths registered</p>
        </div>
        {editable && (
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center justify-center gap-2 w-full sm:w-auto"
          data-testid="create-kiosk-btn"
        >
          <Plus className="w-4 h-4" />
          Add Booth
        </button>
        )}
      </div>

      {/* Search */}
      <div className="relative mb-4 sm:mb-6">
        <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-charcoal-800/40" />
        <input
          type="text"
          placeholder="Search booths..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 sm:pl-12 pr-4 py-2.5 sm:py-3 text-sm sm:text-base bg-white border border-charcoal-900/10 rounded-xl focus:outline-none focus:border-charcoal-900/30"
          data-testid="search-kiosks-input"
        />
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card-dashboard animate-pulse">
              <div className="h-5 sm:h-6 w-28 sm:w-32 bg-gray-200 rounded mb-3"></div>
              <div className="h-4 w-20 sm:w-24 bg-gray-100 rounded"></div>
            </div>
          ))}
        </div>
      ) : filteredKiosks.length === 0 ? (
        <div className="text-center py-12 sm:py-16">
          <Monitor className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-charcoal-800/20 mb-4" />
          <h3 className="font-serif text-lg sm:text-xl text-charcoal-900 mb-2">
            {searchQuery ? 'No booths found' : 'No booths yet'}
          </h3>
          <p className="text-charcoal-800/60 mb-6 text-sm sm:text-base px-4">
            {searchQuery ? 'Try a different search term' : 'Create your first photobooth'}
          </p>
          {!searchQuery && editable && (
            <button onClick={() => setShowCreateModal(true)} className="btn-secondary">
              Create First Booth
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {filteredKiosks.map((kiosk) => (
            <div
              key={kiosk.id}
              onClick={() => navigate(`/admin/kiosks/${kiosk.id}`)}
              className="card-dashboard cursor-pointer hover:shadow-md transition-all group"
              data-testid={`kiosk-card-${kiosk.id}`}
            >
              <div className="flex items-start justify-between mb-3 sm:mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-rose-100 rounded-xl flex items-center justify-center">
                  <Monitor className="w-5 h-5 sm:w-6 sm:h-6 text-charcoal-900" />
                </div>
                <div className="flex items-center gap-1.5 flex-wrap justify-end">
                  {kiosk.camera_mode === 'native' && (
                    <div
                      className="flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-amber-100 text-amber-700"
                      title="Using the ffmpeg Native Camera Helper (WebRTC bypassed)"
                      data-testid={`native-cam-badge-${kiosk.id}`}
                    >
                      <Video className="w-3 h-3" />
                      Native Cam
                    </div>
                  )}
                  <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                    kiosk.status === 'online' 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {kiosk.status === 'online' ? (
                      <Wifi className="w-3 h-3" />
                    ) : (
                      <WifiOff className="w-3 h-3" />
                    )}
                    {kiosk.status}
                  </div>
                </div>
              </div>
              
              <h3 className="font-serif text-base sm:text-lg text-charcoal-900 group-hover:text-rose-500 transition-colors">
                {kiosk.name}
              </h3>
              <div className="flex items-center gap-1 text-xs sm:text-sm text-charcoal-800/60 mt-1">
                <MapPin className="w-3 h-3 sm:w-4 sm:h-4" />
                {kiosk.location}
              </div>
              
              <div className="flex items-center justify-between mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-charcoal-900/5">
                <span className="text-xs sm:text-sm text-charcoal-800/40">Prints: {kiosk.print_count}</span>
                <span className="text-xs sm:text-sm text-charcoal-800/40 font-mono">{kiosk.pairing_code}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl" data-testid="create-kiosk-modal">
            <h3 className="font-serif text-2xl text-charcoal-900 mb-6">Create New Booth</h3>
            
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-charcoal-800 mb-2">Booth Name</label>
                <input
                  type="text"
                  value={newKiosk.name}
                  onChange={(e) => setNewKiosk({ ...newKiosk, name: e.target.value })}
                  required
                  placeholder="e.g., Main Bar Booth"
                  className="w-full px-4 py-3 bg-cream-50 border border-charcoal-900/10 rounded-xl focus:outline-none focus:border-charcoal-900/30"
                  data-testid="kiosk-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-charcoal-800 mb-2">Location</label>
                <input
                  type="text"
                  value={newKiosk.location}
                  onChange={(e) => setNewKiosk({ ...newKiosk, location: e.target.value })}
                  required
                  placeholder="e.g., Mumbai, Maharashtra"
                  className="w-full px-4 py-3 bg-cream-50 border border-charcoal-900/10 rounded-xl focus:outline-none focus:border-charcoal-900/30"
                  data-testid="kiosk-location-input"
                />
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-3 border border-charcoal-900/10 rounded-xl hover:bg-cream-50 transition-colors"
                  data-testid="cancel-create-btn"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 btn-primary disabled:opacity-50"
                  data-testid="submit-create-btn"
                >
                  {creating ? 'Creating...' : 'Create Booth'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default KioskList;
